<template>
  <teleport to="body">
    <div
      v-if="contextMenuState.isOpen && contextElement?.element"
      class="fixed inset-0 z-[99998]"
      :class="{ dark: isDark }"
      @pointerdown.self="close"
      @contextmenu.prevent
    >
      <div
        ref="menuRef"
        class="absolute min-w-[200px] rounded-md border border-gray-200 dark:border-charcoal-600 bg-white dark:bg-charcoal-800 shadow-lg py-1 text-sm text-gray-800 dark:text-gray-100"
        :style="menuPositionStyle"
        @pointerdown.stop
      >
        <button class="context-menu-item" @click="deleteElement">
          <u-icon :name="COMMON_ICONS.delete" class="text-base" />
          <span>Delete</span>
        </button>
        <button class="context-menu-item" @click="copyElement">
          <u-icon name="i-ri-file-copy-2-line" class="text-base" />
          <span>Copy</span>
        </button>
        <button class="context-menu-item" :disabled="!clipboard" @click="pasteElement">
          <u-icon name="i-ri-clipboard-line" class="text-base" />
          <span>Paste</span>
        </button>
        <button class="context-menu-item" @click="duplicateElement">
          <u-icon name="i-ri-file-copy-line" class="text-base" />
          <span>Duplicate</span>
        </button>
        <div class="my-1 border-t border-gray-200 dark:border-charcoal-600" />
        <button
          v-if="isImageElement"
          class="context-menu-item"
          :disabled="!canSnapToBackground"
          @click="snapToBackground"
        >
          <u-icon name="i-ri-focus-3-line" class="text-base" />
          <span>Snap to Background</span>
        </button>
        <div class="px-3 py-1 text-xs font-semibold uppercase text-gray-500 dark:text-gray-300">Position</div>
        <button class="context-menu-item" :disabled="!elementOverlaps.over" @click="reorder(SURFACE_EDITOR_REORDER_ACTIONS.forward)">
          <u-icon name="i-ri-bring-forward" class="text-base" />
          <span>Forward</span>
        </button>
        <button class="context-menu-item" :disabled="!elementOverlaps.under" @click="reorder(SURFACE_EDITOR_REORDER_ACTIONS.backward)">
          <u-icon name="i-ri-send-backward" class="text-base" />
          <span>Backward</span>
        </button>
        <button class="context-menu-item" :disabled="!elementOverlaps.over" @click="reorder(SURFACE_EDITOR_REORDER_ACTIONS.front)">
          <u-icon name="i-ri-bring-to-front" class="text-base" />
          <span>To Front</span>
        </button>
        <button class="context-menu-item" :disabled="!elementOverlaps.under" @click="reorder(SURFACE_EDITOR_REORDER_ACTIONS.back)">
          <u-icon name="i-ri-send-to-back" class="text-base" />
          <span>To Back</span>
        </button>
        <div class="my-1 border-t border-gray-200 dark:border-charcoal-600" />
        <button class="context-menu-item" disabled>
          <u-icon name="i-ri-lock-line" class="text-base" />
          <span>Lock (coming soon)</span>
        </button>
      </div>
    </div>
  </teleport>
</template>

<script setup>
  import {storeToRefs} from 'pinia';
  import {useElementSize, useEventListener, useWindowSize} from '@vueuse/core';
  import {SURFACE_EDITOR_ELEMENT_TYPE, SURFACE_EDITOR_REORDER_ACTIONS} from '~/constants/surface-editor.js';

  const surfaceEditorStore = useSurfaceEditorStore();
  const {contextMenuState, contextMenuElement, clipboard} = storeToRefs(surfaceEditorStore);
  const {isDark} = useSurfaceEditorTheme();

  const surfaceEditorUtils = useSurfaceEditorUtils();

  const menuRef = ref(null);
  const {width: menuWidth, height: menuHeight} = useElementSize(menuRef);
  const {width: windowWidth, height: windowHeight} = useWindowSize();

  const menuPositionStyle = computed(() => {
    const margin = 8;
    const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
    const maxX = Math.max(margin, windowWidth.value - menuWidth.value - margin);
    const maxY = Math.max(margin, windowHeight.value - menuHeight.value - margin);
    const wouldOverflowRight = contextMenuState.value.x + menuWidth.value + margin > windowWidth.value;
    const wouldOverflowBottom = contextMenuState.value.y + menuHeight.value + margin > windowHeight.value;
    const anchorX = wouldOverflowRight ? contextMenuState.value.x - menuWidth.value : contextMenuState.value.x;
    const anchorY = wouldOverflowBottom ? contextMenuState.value.y - menuHeight.value : contextMenuState.value.y;
    const x = clamp(anchorX, margin, maxX);
    const y = clamp(anchorY, margin, maxY);

    return {
      left: `${x}px`,
      top: `${y}px`
    };
  });

  const contextElement = computed(() => contextMenuElement.value);

  const isImageElement = computed(() => contextElement.value?.element?.type === SURFACE_EDITOR_ELEMENT_TYPE.image);
  const hasImageDimensions = computed(() => {
    if (!isImageElement.value) return false;

    const content = contextElement.value?.element?.content;
    return Boolean(content?.image_width && content?.image_height);
  });
  const hasSurfaceDimensions = computed(() => Boolean(contextElement.value?.surface?.width && contextElement.value?.surface?.height));
  const canSnapToBackground = computed(() => isImageElement.value && hasImageDimensions.value && hasSurfaceDimensions.value);

  const elementOverlaps = computed(() => {
    const element = contextElement.value?.element;
    const surface = contextElement.value?.surface;
    if (!element || !surface) return {over: false, under: false};

    const overlaps = surfaceEditorUtils.getOverlappingElements({
      element,
      surface
    });
    const elementOverlapIndex = overlaps.findIndex(overlap => overlap.element.id === element.id);

    return {
      over: elementOverlapIndex < overlaps.length - 1,
      under: elementOverlapIndex > 0
    };
  });

  function close() {
    surfaceEditorStore.closeContextMenu();
  }

  function deleteElement() {
    if (!contextElement.value?.element) return;
    surfaceEditorStore.deleteElement({element: contextElement.value.element});
    close();
  }

  function copyElement() {
    if (!contextElement.value?.element) return;
    surfaceEditorStore.copyElement({element: contextElement.value.element});
    close();
  }

  function pasteElement() {
    surfaceEditorStore.pasteElement();
    close();
  }

  function duplicateElement() {
    if (!contextElement.value?.element) return;
    surfaceEditorStore.duplicateElement({element: contextElement.value.element});
    close();
  }

  function snapToBackground() {
    if (!contextElement.value?.element) return;

    surfaceEditorStore.snapImageToBackground({element: contextElement.value.element});
    surfaceEditorStore.reorderElements({element: contextElement.value.element, action: SURFACE_EDITOR_REORDER_ACTIONS.back});
    close();
  }

  function reorder(action) {
    if (!contextElement.value?.element) return;
    surfaceEditorStore.reorderElements({element: contextElement.value.element, action});
    close();
  }

  useEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      close();
    }
  });
</script>

<style scoped>
.context-menu-item {
  @apply flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-gray-100 dark:hover:bg-charcoal-700 disabled:cursor-not-allowed disabled:opacity-50;
}
</style>
